/**
 * FloorNameCombobox — a type-to-search + add-if-missing CREATABLE combobox for a
 * floor / surface name (owner directive 2026-06-26, AD-048). Replaces the fixed
 * floor <select> on BOTH grid surfaces (Properties registry floors + project
 * Technical Intake) so a vacant-plot candidate can be named "Site / plot map"
 * (or any free value) instead of being forced into a building-floor preset.
 *
 * Presets are suggestions; free text is allowed; "Site / plot map" is a
 * first-class suggestion. The caller maps the chosen LABEL back to a registry
 * FloorId (propertyModel.resolveFloorChoice) — a custom label still grids.
 */
import { useEffect, useRef, useState } from 'react';
import { FLOOR_LABELS, FLOOR_IDS } from '../lib/propertyModel';

/** The default suggestion list: a site/plot map first, then the building floors. */
export const FLOOR_NAME_SUGGESTIONS: string[] = [
  'Site / plot map',
  ...FLOOR_IDS.map((id) => FLOOR_LABELS[id]),
];

export default function FloorNameCombobox({
  value,
  onChange,
  onCommit,
  suggestions = FLOOR_NAME_SUGGESTIONS,
  usedLabels = [],
  placeholder = 'Type a floor / surface name…',
  style,
  ariaLabel,
}: {
  value: string;
  /** fires on every keystroke (free text). */
  onChange: (v: string) => void;
  /** fires when a value is COMMITTED (pick a suggestion, "Add", or Enter). */
  onCommit?: (label: string) => void;
  suggestions?: string[];
  /** labels already used (shown dimmed + tagged, still pickable). */
  usedLabels?: string[];
  placeholder?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  const q = value.trim().toLowerCase();
  const matches = suggestions.filter((s) => !q || s.toLowerCase().includes(q));
  const exact = suggestions.some((s) => s.toLowerCase() === q);
  const usedSet = new Set(usedLabels.map((l) => l.toLowerCase()));

  function commit(label: string) {
    onChange(label);
    onCommit?.(label);
    setOpen(false);
  }

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    border: '1px solid var(--rule-strong, #c8ccd2)',
    borderRadius: 'var(--r-sm, 6px)',
    padding: '8px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    background: '#fff',
    color: 'var(--ink)',
    ...style,
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        style={fieldStyle}
        value={value}
        aria-label={ariaLabel || 'Floor or surface name'}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            e.preventDefault();
            commit(value.trim());
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && (matches.length > 0 || (q && !exact)) && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            zIndex: 30,
            maxHeight: 240,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid var(--rule-strong, #c8ccd2)',
            borderRadius: 'var(--r-sm, 6px)',
            boxShadow: 'var(--e-2, 0 6px 20px rgba(0,0,0,0.12))',
            padding: 4,
          }}
        >
          {matches.map((s) => {
            const isUsed = usedSet.has(s.toLowerCase());
            return (
              <button
                key={s}
                type="button"
                onClick={() => commit(s)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: 'var(--r-sm, 6px)',
                  padding: '7px 10px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  color: isUsed ? 'var(--ink-mute, #9aa0a6)' : 'var(--ink)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--inset, #f6f7f9)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span>{s}</span>
                {isUsed && (
                  <span style={{ fontSize: 10.5, color: 'var(--ink-mute)' }}>· already added</span>
                )}
              </button>
            );
          })}
          {q && !exact && (
            <button
              type="button"
              onClick={() => commit(value.trim())}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                borderTop: matches.length ? '1px solid var(--rule)' : 'none',
                borderRadius: 'var(--r-sm, 6px)',
                padding: '7px 10px',
                fontSize: 13,
                fontFamily: 'inherit',
                color: 'var(--accent, #2f5d8a)',
                cursor: 'pointer',
                fontWeight: 600,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--inset, #f6f7f9)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              + Add “{value.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
